import { type Editor } from '@tiptap/react';
import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { resetSelectedItemsAction } from '@air/redux-selected-items';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  isWorkspaceAgentComposerSelectionDragDataTransfer,
  WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE,
} from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragPayload';
import {
  endWorkspaceAgentComposerSelectionDrag,
  getWorkspaceAgentComposerSelectionDragPreviewLabel,
  getWorkspaceAgentComposerSelectionDragSessionId,
  isWorkspaceAgentComposerSelectionDragSessionActive,
} from '~/components/v2/components/workspace/workspace-agent-panel/drag/workspaceAgentComposerSelectionDragSession';
import { useIsWorkspaceAgentPanelOverAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelOverAssetView';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { insertWorkspaceAgentComposerSelectionFromDrop } from '~/components/v2/components/workspace/workspace-agent-panel/utils/insertWorkspaceAgentComposerSelectionFromDrop';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';

export type UsePrivateWorkspaceAgentComposerSelectionDropParams = {
  editor: Editor | null;
  isComposerDisplayed: boolean;
  isComposerEnabled: boolean;
};

export const usePrivateWorkspaceAgentComposerSelectionDrop = ({
  editor,
  isComposerDisplayed,
  isComposerEnabled,
}: UsePrivateWorkspaceAgentComposerSelectionDropParams) => {
  const dispatch = useDispatch();
  const { workspaceId } = usePrivateWorkspaceAgentPanelSession();
  const boardId = useURLBoardIdSelector();
  const currentViewTypeName = useSelector(currentViewTypeNameSelector);
  const { isWorkspaceAgentPanelOverAssetView } = useIsWorkspaceAgentPanelOverAssetView();
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSuppressingEditorAgentDrag, setIsSuppressingEditorAgentDrag] = useState(false);
  const isComposerEnabledRef = useRef(isComposerEnabled);

  isComposerEnabledRef.current = isComposerEnabled;

  const canAcceptDrop = useCallback(
    (dataTransfer: DataTransfer | null) => {
      if (!isComposerDisplayed || !isComposerEnabled || isWorkspaceAgentPanelOverAssetView) {
        return false;
      }

      if (!boardId) {
        return false;
      }

      if (currentViewTypeName !== ViewTypeName.gallery && currentViewTypeName !== ViewTypeName.table) {
        return false;
      }

      return dataTransfer?.types.includes(WORKSPACE_AGENT_COMPOSER_SELECTION_DROP_TYPE) ?? false;
    },
    [boardId, currentViewTypeName, isComposerDisplayed, isComposerEnabled, isWorkspaceAgentPanelOverAssetView],
  );

  const clearAgentDragUiState = useCallback(() => {
    setIsDragOver(false);
    setIsSuppressingEditorAgentDrag(false);
  }, []);

  const handleAgentSelectionDragOverCapture = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (!isWorkspaceAgentComposerSelectionDragDataTransfer(event.dataTransfer)) {
        clearAgentDragUiState();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsSuppressingEditorAgentDrag(true);
      editor?.commands.blur();

      if (!canAcceptDrop(event.dataTransfer)) {
        setIsDragOver(false);
        event.dataTransfer.dropEffect = 'none';
        return;
      }

      event.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    },
    [canAcceptDrop, clearAgentDragUiState, editor],
  );

  const handleAgentSelectionDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const relatedTarget = event.relatedTarget;

      // Match canvas/chat drop targets: only clear when the pointer entered a node outside
      // the pane. `relatedTarget` is often null during internal moves (e.g. Firefox), which
      // would flicker the overlay if we treated that as leaving.
      if (relatedTarget instanceof Node && !event.currentTarget.contains(relatedTarget)) {
        clearAgentDragUiState();
      }
    },
    [clearAgentDragUiState],
  );

  useEffect(() => {
    const onDragEnd = () => {
      clearAgentDragUiState();

      if (isWorkspaceAgentComposerSelectionDragSessionActive()) {
        endWorkspaceAgentComposerSelectionDrag();
      }
    };

    document.addEventListener('dragend', onDragEnd, true);

    return () => {
      document.removeEventListener('dragend', onDragEnd, true);
    };
  }, [clearAgentDragUiState]);

  const handleDropCapture = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const isAgentSelectionDrag = isWorkspaceAgentComposerSelectionDragDataTransfer(event.dataTransfer);
      const hadActiveDragSession = isWorkspaceAgentComposerSelectionDragSessionActive();

      clearAgentDragUiState();

      if (isAgentSelectionDrag) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!isAgentSelectionDrag) {
        return;
      }

      if (!canAcceptDrop(event.dataTransfer) || !editor) {
        endWorkspaceAgentComposerSelectionDrag();
        return;
      }

      const sessionIdAtDrop = getWorkspaceAgentComposerSelectionDragSessionId();
      const docAtDrop = editor.state.doc;

      const shouldCompleteInsert = (): boolean =>
        isComposerEnabledRef.current && getWorkspaceAgentComposerSelectionDragSessionId() === sessionIdAtDrop;

      void (async () => {
        try {
          if (!shouldCompleteInsert()) {
            return;
          }

          const didInsert = await insertWorkspaceAgentComposerSelectionFromDrop({
            dataTransfer: event.dataTransfer,
            docAtDrop,
            editor,
            hadActiveDragSession,
            sessionIdAtDrop,
            shouldCompleteInsert,
            workspaceId,
          });

          if (!shouldCompleteInsert()) {
            return;
          }

          if (didInsert) {
            resetSelectAll();
            dispatch(resetSelectedItemsAction());
          }
        } catch (error) {
          reportErrorToBugsnag({
            context: 'Workspace agent composer selection drop',
            error,
          });
        } finally {
          if (getWorkspaceAgentComposerSelectionDragSessionId() === sessionIdAtDrop) {
            endWorkspaceAgentComposerSelectionDrag();
          }
        }
      })();
    },
    [canAcceptDrop, clearAgentDragUiState, dispatch, editor, resetSelectAll, workspaceId],
  );

  const selectionDropOverlayDescription = getWorkspaceAgentComposerSelectionDragPreviewLabel() ?? '';

  return {
    handleAgentSelectionDragOverCapture,
    handleAgentSelectionDragLeave,
    handleDropCapture,
    isDragOver,
    isSuppressingEditorAgentDrag,
    selectionDropOverlayDescription,
  };
};
